import { Icon } from '../../../components/icons';
import { FeaturePageLayout, type FeatureContent } from './FeaturePageLayout';
import { WarmupStory } from './stories/WarmupStory';

/**
 * Marketing page for the Warmup add-on. Positioned as the "fix" to Inbox
 * Placement's "diagnose" half, plus the multi-mailbox use case the user called
 * out as a key differentiator. Warmup is sold PER MAILBOX across three SieveGuard
 * tiers (Warm-Up Starter / Growth / Scale) — we never surface the underlying
 * provider's plan naming, and the wholesale cost / margin is never exposed.
 */
const CONTENT: FeatureContent = {
  kind: 'warmup_inbox',
  slug: 'warmup',
  story: <WarmupStory />,
  title: 'Warmup',
  tagline: 'Cold-email sequence warmup, ESP-specific warmup, multi-language content — priced per mailbox.',
  eyebrow: 'Add-on · per mailbox',
  description:
    "Connect Gmail, Microsoft 365, or any SMTP/IMAP mailbox and we auto-warm them via partner send/reply loops. Each mailbox runs on the tier you choose for it — Warm-Up Starter, Growth, or Scale — so you can mix a light ramp on one mailbox with full advanced controls on another, and you only pay for the mailboxes you're actually warming. Sender reputation 0-100 per ESP (Google / Microsoft / Other), per-mailbox health-check, multi-language warmup content. The natural fix when Inbox Placement diagnoses a deliverability problem: most \"we're landing in spam\" issues are mailbox-reputation issues before they're domain issues. IP-level warmup is on our near-term roadmap.",
  icon: <Icon.Activity width={48} height={48} />,
  accent: 'allow',
  heroBg: '/heroes/feat-warmup.webp',
  pillars: [
    {
      title: 'Cold-email sequence warmup',
      body: 'A configurable send/reply sequence runs daily on every mailbox — progressive ramp, flat, or randomised. Set starting baseline, daily increase, max sends/day, and reply rate per inbox; bulk-apply settings across a fleet of 50 in one modal.',
    },
    {
      title: 'ESP-specific warmup',
      body: 'Bias the warmup loop toward the ESPs your prospects use most — Google, Microsoft, or both. Daily refresh of overall + per-ESP reputation; the same dashboard that tells you "your fleet averages 87/100" also flags the one inbox at 42/100 dragging the average down.',
    },
    {
      title: 'Multi-language warmup content',
      body: '23 supported languages — English, German, French, Spanish, Italian, Polish, Portuguese, Dutch, Russian, Turkish, and more. Match the warmup content language to your real campaign language so the engagement signal ESPs see is consistent with the conversations you actually have.',
    },
    {
      title: 'One-click Google connect, multi-mailbox by design',
      body: 'No copy-pasting OAuth tokens — tenants click "Continue with Google", grant consent on the real Google screen, and we wire the inbox in one round-trip. Microsoft + SMTP also supported, with CSV bulk-connect for fleets of 50+.',
    },
  ],
  steps: [
    {
      title: 'Choose a tier per mailbox',
      body: 'When you add a mailbox you pick its tier — Warm-Up Starter (up to 75 warmup emails/day), Warm-Up Growth (up to 250/day, plus your own templates, warmup topics, language warmup, custom settings, per-template stats, ESP-specific and scheduled warmup), or Warm-Up Scale (up to 1,000/day with every advanced control). Mix tiers freely across mailboxes; you only pay for mailboxes you keep warming.',
    },
    {
      title: 'Connect mailboxes',
      body: 'One-click for Google Workspace, paste OAuth for Microsoft, or full SMTP+IMAP credentials. CSV-bulk-connect for fleet onboarding (drop a 30-row file, watch them all provision).',
    },
    {
      title: 'Start warming',
      body: 'Sensible defaults per tier (reply-rate ceilings rise with the tier — Starter 25%, Growth 45%, Scale 50%+). Tune frequency, schedule, language, ESP priority, and AI topics per inbox in the Settings tab — advanced controls unlock on Growth and Scale. Or apply settings to N inboxes in bulk.',
    },
    {
      title: 'Monitor reputation',
      body: 'Per-ESP reputation chart on the inbox detail page, per-mailbox health-check (MX / SPF / DMARC / blacklists / warmup-days), 30-day landed-inbox vs spam strip. Delete a mailbox the moment you no longer need it — billing for it stops immediately.',
    },
  ],
  included: [
    {
      scope: 'trial',
      heading: 'During Trial',
      subtext: 'Requires an active subscription — not part of the free trial',
      items: [
        'Not available during the platform trial — Warmup needs an active plan',
        'Pick a plan on /pricing, then attach the first mailbox to unlock Warmup',
        'Warmup billing starts only when you connect that first mailbox',
      ],
    },
    {
      scope: 'pro',
      items: [
        'Your plan may bundle a starting mailbox allowance',
        'Per-mailbox tiers stack on top',
        'No long-term commitment — billed per mailbox, monthly',
      ],
    },
    {
      scope: 'addon',
      items: [
        'Warm-Up Starter / Growth / Scale — choose per mailbox',
        'Bulk-connect via CSV + bulk apply-settings',
        'Own templates, topics, language, ESP-specific & scheduled warmup on Growth & Scale',
      ],
    },
  ],
  faq: [
    {
      q: 'Do you do IP warmup?',
      a: 'Not yet — IP-level warmup is on our near-term roadmap. Today this add-on handles mailbox-level reputation, which is what cold-email teams sending from Gmail / Microsoft / Workspace mailboxes actually need. IP warmup matters when you send from a dedicated sending IP (e.g. SendGrid Pro+ / Mailgun Dedicated / SES with a reserved IP), and we plan to add it as a sibling capability so the same dashboard covers both.',
    },
    {
      q: "What's actually warming?",
      a: "The mailbox-level reputation of each connected address. We orchestrate send/reply loops between your address and our partner network of inboxes; ESPs see consistent engaged conversations, mailbox reputation climbs, your real campaigns land better. Mailbox-level warmup is what most cold-email teams need; IP-level warmup (useful for tenants on dedicated sending IPs) is on our near-term roadmap.",
    },
    {
      q: 'How does Warmup pair with Inbox Placement?',
      a: 'Inbox Placement diagnoses where mail lands; Warmup fixes the underlying mailbox reputation that drives placement. The recommended workflow: run an Inbox Placement test → see Gmail = 80% inbox, Outlook = 30% → connect the relevant mailboxes to Warmup → re-test after 2-3 weeks → see the lift on the Inbox Placement trend curve.',
    },
    {
      q: 'How does per-mailbox pricing work?',
      a: (mode) =>
        `Each warming mailbox is billed at the tier you chose for it (Warm-Up Starter, Growth, or Scale), monthly via Stripe. Your invoice shows one line per tier with the quantity of mailboxes on it. ${
          mode.chargeUpgradesImmediately
            ? 'Adding a mailbox is charged in advance the moment you connect it (pay-first, warm-second). Deleting a mailbox stops warming immediately and credits the unused portion of the tier onto your next invoice.'
            : 'Adding or removing a mailbox pro-rates onto your next invoice — no up-front charge, no refund cheque.'
        } There's no pause switch — to stop paying for a mailbox you delete it, and billing for it ends immediately. No slot cap to manage: add as many mailboxes as you need, each on the tier that fits it.`,
    },
    {
      q: 'Can I bulk-onboard 30 mailboxes from a CSV?',
      a: 'Yes — the bulk connect dialog accepts a 14-column CSV (email + sender first/last + timezone + smtp_host/port/tls/user/pass + imap_host/port/tls/user/pass). Per-row failures surface with the line number so you can fix and re-upload just the bad rows.',
    },
    {
      q: 'What about custom email templates?',
      a: "The Warm-Up Growth and Scale tiers can replace the AI-generated warmup content with your own templates (HTML or plain text, with {{ receiver }} interpolation). Per-tenant ownership is enforced via inbox association — you only see your own templates. Manage them from /app/warmup/templates.",
    },
  ],
  addonKind: 'warmup_inbox',
  relatedFeatures: [
    {
      slug: 'inbox-placement',
      title: 'Inbox Placement',
      tagline: 'Diagnose the deliverability problem Warmup is going to fix.',
    },
    {
      slug: 'domain-health',
      title: 'Domain Health',
      tagline: 'SPF / DKIM / DMARC posture is the other half of deliverability.',
    },
    {
      slug: 'blacklist-checks',
      title: 'Blacklist Checks',
      tagline: 'Blacklist hits drag mailbox reputation even with perfect warmup.',
    },
  ],
};

export function WarmupFeaturePage() {
  return <FeaturePageLayout content={CONTENT} />;
}

// Default export required by React Router framework-mode route modules.
export default WarmupFeaturePage;
